import { useState } from "react";
import { Button, Grid, Tab, Tabs } from "@mui/material";
import { useTracking } from "react-tracking";
import { makeStyles } from "@/compat/muiStyles";
import CardBase from "../../../components/cards/CardBase";
import { Link, useRouteMatch } from "@/compat/router";
import RiskAssessmentAdminTemplatesTableCard from "../../components/RiskAssessmentAdminTemplatesTableCard";
import ShareGroupsTableCard from "../../components/riskassessments/ShareGroupsTableCard";
import BrandingTableCard from "../../components/riskassessments/BrandingTableCard";
import PageBase from "../../../components/PageBase";
import PageTitle from "../../components/PageTitle";
import { Create } from "@mui/icons-material";
import { useAppSelector } from "@/redux/hooks";
import { useAuth } from "../../../contexts/authContext";
import RequiresRoleWrapper from "../../../components/RequiresRoleWrapper";
import riskAssessmentSetupTabs from "../../constants/riskAssessmentSetupTabConstants";

const useStyles = makeStyles((theme) => ({
  tabs: {
    borderBottom: `1px solid ${theme.palette.divider}`,
    marginBottom: theme.spacing(2),
  },
  tab: {
    textTransform: "none",
  },
}));

function RiskAssessmentsAdmin() {
  const classes = useStyles();
  const { Track } = useTracking({
    page: `Admin: Risk Assessments`,
  });
  const match = useRouteMatch();
  const { selectedAccount } = useAppSelector((state) => state.account);
  const { isTemplateAdminForSites, isSafetyNestAdministratorForSites, isWorknestUser } = useAuth();
  const { path } = match;

  const isTemplateAdmin = isTemplateAdminForSites(
    selectedAccount?.childExternalIds
  );

  const isSafetyNestAdmin = isSafetyNestAdministratorForSites(
    selectedAccount?.childExternalIds
  );

  // Branding is open to SafetyNest administrators, who don't necessarily amend
  // templates, so the page opens wider than the tabs inside it.
  const canAmendTemplates = isTemplateAdmin || isWorknestUser;

  const [selectedTab, setSelectedTab] = useState(
    canAmendTemplates
      ? riskAssessmentSetupTabs.TEMPLATES
      : riskAssessmentSetupTabs.BRANDING
  );

  return (
    <RequiresRoleWrapper hasRole={canAmendTemplates || isSafetyNestAdmin}>
      <PageBase>
        <Track>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12 }}>
              <CardBase
                title={
                  <PageTitle icon={<Create />} title="Risk Assessments Setup" />
                }
                rightComponent={
                  canAmendTemplates &&
                  selectedTab === riskAssessmentSetupTabs.TEMPLATES && (
                    <Button
                      component={Link}
                      to={`${path}/templatebuilder`}
                      variant="contained"
                      color="primary"
                    >
                      Create Template
                    </Button>
                  )
                }
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Tabs
                className={classes.tabs}
                value={selectedTab}
                onChange={(_, value) => setSelectedTab(value)}
                textColor="primary"
                indicatorColor="primary"
              >
                {canAmendTemplates && (
                  <Tab
                    className={classes.tab}
                    value={riskAssessmentSetupTabs.TEMPLATES}
                    label="Templates"
                  />
                )}
                <Tab
                  className={classes.tab}
                  value={riskAssessmentSetupTabs.SHARE_GROUPS}
                  label="Share groups"
                />
                <Tab
                  className={classes.tab}
                  value={riskAssessmentSetupTabs.BRANDING}
                  label="Branding"
                />
              </Tabs>
              {canAmendTemplates && selectedTab === riskAssessmentSetupTabs.TEMPLATES && (
                <RiskAssessmentAdminTemplatesTableCard />
              )}
              {selectedTab === riskAssessmentSetupTabs.SHARE_GROUPS && (
                <ShareGroupsTableCard />
              )}
              {selectedTab === riskAssessmentSetupTabs.BRANDING && (
                <BrandingTableCard />
              )}
            </Grid>
          </Grid>
        </Track>
      </PageBase>
    </RequiresRoleWrapper>
  );
}

export default RiskAssessmentsAdmin;
